import { useMemo } from 'react';
import { Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import { BulletList, Exercise, LabelBody, ProgressBar } from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseStatus } from '../../interfaces';
import {
    stringifyArray,
    parseArray,
    normalizeInput,
} from '../../useSyncedState';
import { Module4Exercise1Graph } from './Module4Exercise1Graph';
import { Page1 } from './Module4Exercise1Page1';
import { Page2 } from './Module4Exercise1Page2';
import { Page3 } from './Module4Exercise1Page3';
import { Page4 } from './Module4Exercise1Page4';
import { Page5 } from './Module4Exercise1Page5';
import { hydrateRatings, parseRatings, stringifyRatings } from './Rating';

export const importantAreasKey = 'importantAreas';
export const ratingsImportanceKey = 'ratingsImportance';
export const ratingsTimeKey = 'ratingsTime';
const reflectionKey = 'reflection';

export const exerciseData41: ExerciseData = {
    ExerciseView: Module4Exercise1,
    dataKeys: [
        importantAreasKey,
        ratingsImportanceKey,
        ratingsTimeKey,
        reflectionKey,
    ],
    haveDataForLogbook(exercise) {
        const importantAreas = parseArray(exercise.Data[importantAreasKey]);
        const ratingsImportance = parseRatings(
            exercise.Data[ratingsImportanceKey],
        );
        const ratingsTime = parseRatings(exercise.Data[ratingsTimeKey]);
        return (
            !!importantAreas.length ||
            !!ratingsImportance.length ||
            !!ratingsTime.length
        );
    },
    LogbookHeader({ exercise, ...props }) {
        const importantAreas = parseArray(exercise.Data[importantAreasKey]);
        const ratingsImportance = parseRatings(
            exercise.Data[ratingsImportanceKey],
        );
        const ratingsTime = parseRatings(exercise.Data[ratingsTimeKey]);
        return (
            <Module4Exercise1Graph
                {...props}
                importantAreas={importantAreas}
                ratingsImportance={ratingsImportance}
                ratingsTime={ratingsTime}
            />
        );
    },
    questions: [
        {
            dataKeys: [reflectionKey],
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvordan er balancen?"
                    description={
                        <>
                            <LabelBody>
                                For at føle dig mindre presset er det smart at
                                bruge tid på det, der virkelig betyder noget for
                                dig.
                            </LabelBody>
                            <LabelBody>
                                Kig nu på dit kompas og tænk over:
                                <BulletList
                                    items={[
                                        'Hvor bruger du meget tid på noget, der ikke føles så vigtigt?',
                                        'Hvor bruger du tid på noget, der føles meget vigtigt?',
                                        'Er der noget ved kompasset, der overrasker dig, eller er det som forventet?',
                                    ]}
                                />
                            </LabelBody>
                        </>
                    }
                />
            ),
        },
    ],
};

function Module4Exercise1({ exercise, onContinue }: ExerciseViewProps) {
    const importantAreas = useMemo(
        () => parseArray(exercise.Data[importantAreasKey]),
        [exercise.Data[importantAreasKey]],
    );
    const ratingsImportance = useMemo(
        () =>
            hydrateRatings(
                parseRatings(exercise.Data[ratingsImportanceKey]),
                importantAreas,
            ),
        [exercise.Data[ratingsImportanceKey], importantAreas],
    );
    const ratingsTime = useMemo(
        () =>
            hydrateRatings(
                parseRatings(exercise.Data[ratingsTimeKey]),
                importantAreas,
            ),
        [exercise.Data[ratingsTimeKey], importantAreas],
    );
    return (
        <Exercise title="Mit indre kompas">
            <ProgressBar current={exercise.CurrentPage} total={5} />
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            selectedItems={exercise.Data[importantAreasKey]}
                            onContinue={(items) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [importantAreasKey]:
                                            stringifyArray(items),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            ratings={ratingsImportance}
                            onContinue={(ratings) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                    data: {
                                        [ratingsImportanceKey]:
                                            stringifyRatings(ratings),
                                    },
                                })
                            }
                        />
                    ),
                    4: (
                        <Page4
                            ratings={ratingsTime}
                            onContinue={(ratings) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                    data: {
                                        [ratingsTimeKey]:
                                            stringifyRatings(ratings),
                                    },
                                })
                            }
                        />
                    ),
                    5: (
                        <Page5
                            importantAreas={importantAreas}
                            ratingsImportance={ratingsImportance}
                            ratingsTime={ratingsTime}
                            reflection={exercise.Data[reflectionKey] ?? ''}
                            onContinue={(reflection) =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '4.1.completed',
                                    data: {
                                        [reflectionKey]:
                                            normalizeInput(reflection),
                                    },
                                })
                            }
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}
